import { useState } from 'react'
import type { Page, CreatePageInput, UpdatePageInput } from '../model/contracts'
import { PageForm } from './PageForm'
import { Button } from '../../../shared/ui/Button'

interface PageListProps {
  pages: Page[]
  onCreate: (data: CreatePageInput) => void
  onUpdate: (data: UpdatePageInput) => void
  onDelete: (id: string) => void
}

export function PageList({ pages, onCreate, onUpdate, onDelete }: PageListProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const handleEdit = (page: Page) => {
    setEditingId(page.id)
    setShowForm(false)
  }

  const handleCancel = () => {
    setEditingId(null)
    setShowForm(false)
  }

  const handleSubmit = (data: CreatePageInput | UpdatePageInput) => {
    if ('id' in data) {
      onUpdate(data)
      setEditingId(null)
    } else {
      onCreate(data as CreatePageInput)
      setShowForm(false)
    }
  }

  const editingPage = editingId ? pages.find((p) => p.id === editingId) : undefined

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Monitoring Pages</h2>
        {!showForm && !editingId && (
          <Button onClick={() => setShowForm(true)}>Add Page</Button>
        )}
      </div>

      {(showForm || editingPage) && (
        <div
          style={{
            padding: 24,
            borderRadius: 12,
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
          }}
        >
          <PageForm page={editingPage} onSubmit={handleSubmit} onCancel={handleCancel} />
        </div>
      )}

      {pages.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            borderRadius: 12,
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
            color: 'var(--muted)',
          }}
        >
          <div style={{ fontSize: 16, marginBottom: 8 }}>No pages yet</div>
          <div style={{ fontSize: 14 }}>Add your first monitoring target page</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pages.map((page) => (
            <div
              key={page.id}
              style={{
                padding: 20,
                borderRadius: 12,
                border: '1px solid var(--panel-border)',
                background: 'var(--panel)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 8,
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                    {page.name}
                  </h3>
                  <span
                    className="bg-accent-tint"
                    style={{
                      padding: '4px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'var(--accent)',
                    }}
                  >
                    {page.platform}
                  </span>
                </div>
                <div style={{ marginBottom: 8, fontSize: 14, color: 'var(--muted)' }}>
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--accent)', textDecoration: 'none' }}
                  >
                    {page.url}
                  </a>
                </div>
                {page.description && (
                  <div style={{ fontSize: 14, color: 'var(--muted)' }}>
                    {page.description}
                  </div>
                )}
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 12,
                    color: 'var(--muted)',
                    opacity: 0.7,
                  }}
                >
                  Created {new Date(page.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="neutral" onClick={() => handleEdit(page)}>
                  Edit
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm(`Delete page "${page.name}"?`)) {
                      onDelete(page.id)
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
